
import './who-we-are.scss';
import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Trans, useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import StopMotionSvg from '../../components/StopMotionSvg';
import DashedDrawedSvg from '../../components/DashedDrawedSvg';
import * as images from './who-we-are-media';
import CommonButton from '../../components/button/button';
import { pages } from '../../general.models';
import useDeviceDetection from '../../hooks/useDeviceDetection';


function WhoWeAre() {
    const { reloadState, relativeVh } = useOutletContext();

    const { t } = useTranslation();
    const [isRtl, setIsRtl] = useState(1);
    const [intervalSection2, setIntervalSection2] = useState(null);
    const deviceType = useDeviceDetection();

    useLayoutEffect(() => {
        window.scrollTo({ top: 0 });
        reloadState[1](false);

    }, []);

    useGSAP(() => {

        //#region section 1

        gsap.to('#who-we-are-header', {
            scrollTrigger: {
                trigger: '#section1',
                start: 'top top',
                end: 'bottom top',
                scrub: true,
            },
            scale: 0.61
        });
        //#endregion

        //#region section 2
        const tl = gsap.timeline();
        ScrollTrigger.create({
            animation: tl,
            trigger: '.section-2',
            start: "top 70%",
            end: '10% 20%',
            scrub: true
        });
        tl.fromTo(".section-2 .row .item",
            {
                autoAlpha: 0,
                y: 100,
            },
            {
                autoAlpha: 1,
                y: 0,
                duration: 1,
                stagger: .35
            }
        )
            .add(() => setIntervalSection2(i => !i ? 400 : null), '-=0.2')

        //#endregion


        // section 8
        gsap.timeline({ repeat: -1 })
            .set('.section-14-images .hand-pointing', { y: 30, x: 13 }, 0)
            .set('.section-14-images .hand-pointing', { y: 60, x: 40 }, 0.8)
            .set('.section-14-images .hand-pointing', { y: 30, x: 13 }, 1.6)



        //#region dancing items

        gsap.timeline({ repeat: -1 })
            .to('.key', { scale: 1.2 }, 0)
            .to('.key', { scale: 1 }, 1.4)

        gsap.timeline({ repeat: -1 })
            .to('.play-circle-button, .button', { scale: 1.3 }, 0)
            .to('.play-circle-button, .button', { scale: 1 }, 1.2)

        //#endregion


        setTimeout(() => {
            // Ensure ScrollTrigger recalculates positions
            ScrollTrigger.refresh();
        }, 100);

    }, { dependencies: [] })



    return (
        <div className='who-we-are-component'>
            {/* section 1 */}
            <section id="section-1" className='section-1'>
                <h1 id="who-we-are-header" className='headline'>
                    <Trans i18nKey='who-we-are.title'>
                        <span className='intro-title'></span>
                    </Trans>
                </h1>
            </section>

            {/* section 4 */}
            <section className='section-4' id="section4">
                <div className='sub-title'>
                    <Trans i18nKey={deviceType?.isMobile ? "who-we-are.section-1-title-mobile" : "who-we-are.section-1-title"}>
                        <span className='marked-text'></span>
                    </Trans>
                </div>
                <img src={images.manWithMaskSection4} alt='' />
            </section>

            {/* section 2 */}
            <section className='section-2' id="section2">
                <div className='text-run'>
                    {t('who-we-are.bullets-section.description')}
                </div>
                <div className='courses-types' id='courses-types'>
                    <div className="row">
                        {pages.slice(1, 7).map((item, index) => {
                            let line = images.section2Images[index];
                            return (
                                <div className={`item ${line.name}`} key={`item${index}`}>
                                    <div className='dancing-lines-container'
                                    >
                                        <StopMotionSvg
                                            shapes={line.shapes}
                                            width={line.width}
                                            height={line.height}
                                            interval={intervalSection2}
                                        />
                                    </div>
                                    <div className={`related-image ${item.name}`} key={`item${index}`}>
                                        <img src={line.img} alt={t(item.text)} />
                                    </div>
                                    <p className={`${line.name}-p text-run`} >{t(item.text)}</p>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <p className='text-run'>{t('who-we-are.bullets-section.and-more')}</p>
            </section>

            {/* section 3 */}
            {deviceType?.isMobile ?
                <div className="mobile-dash-line" id="mobile-dash-line-1" >
                    <DashedDrawedSvg svgWidth="195" svgHeight="202"
                        d="M13.5282 54.52C38.9556 1.25039 60.5127 163.604 97.221 79.3794C113.281 42.5302 182.436 141.28 182.649 157.704"
                        strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                        trigger='#mobile-dash-line-1' startTrigger="top 80%" endTrigger="bottom 20%" />
                </div> : <section className='section-3' id="section3">
                    <div className='transition-1' id="transition-1">
                        <DashedDrawedSvg svgWidth="305" svgHeight="261" strokeDasharray={490}
                            d="M5 23.5626C66.1959 -71.0616 67.224 241.606 157.364 91.1387C196.801 25.3077 303.532 229.217 299.91 260.366"
                            trigger='#section3' startTrigger="top 85%" endTrigger="bottom 10%" />
                    </div>
                </section>}

            {/* section 5 */}
            <section className='section-5' id="section5">
                <p className='head-sub'>{t('who-we-are.section-expertise.title')}
                </p>
                <p className='sub-title'>
                    {t('who-we-are.section-expertise.description1')}
                </p>
                <img src={deviceType?.isMobile ? images.completionSection8Mobile : images.completionSection8} alt='' />
                <p className='text-run'>
                    {t('who-we-are.section-expertise.description2')}
                </p>
            </section>


            {/* section 6 */}
            {deviceType?.isMobile ?
                <div className="mobile-dash-line" id="mobile-dash-line-2" >
                    <DashedDrawedSvg svgWidth="195" svgHeight="201"
                        d="M160.311 22.3536C116.52 -17.226 158.388 141.111 92.3821 77.2006C63.5037 49.2393 37.1464 166.879 43.2019 182.148"
                        strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                        trigger='#mobile-dash-line-2' startTrigger="top 85%" endTrigger="bottom 20%" />
                </div> : <section className='section-6' id="section6">
                    <div className='transition-1' id="transition-1">
                        <DashedDrawedSvg svgWidth="305" svgHeight="261" strokeDasharray={490}
                            d="M300 23.4064 C238.804 -71.2179 237.776 241.45 147.636 90.9824 C108.199 25.1514 1.46803 229.061 5.08995 260.21"
                            trigger='#section6' startTrigger="top 85%" endTrigger="bottom 10%" />
                    </div>
                </section>
            }
            {/* section 7 */}
            <section className='section-7' id="section7">

                <p className='text-run'>
                    {t('who-we-are.section-main-content.paragraph')}
                </p>
                <p className='text-run'>
                    <Trans i18nKey='who-we-are.section-main-content.bottom-line'>
                        <span className='head-sub'></span>
                    </Trans>
                </p>
                <img src={images.mansImg} alt='' />


            </section>




            {deviceType?.isMobile &&
                <div className="mobile-dash-line" id="mobile-dash-line-3">
                    <DashedDrawedSvg svgWidth="201" svgHeight="196"
                        d="M63.1351 8.37195
C115.955 -17.9774 33.0592 123.274 113.817 79.462
C149.15 60.294 142.91 180.689 132.972 193.768"    strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                        trigger='#mobile-dash-line-3' startTrigger="top 85%" endTrigger="bottom 10%" />
                </div>
            }

            {/* section 8 */}
            < section className='section-14' id='section14' >
                <div className='yalla'>
                    <div className='sub-title'>
                        <Trans i18nKey="who-we-are.section-finaly.closingNote">
                            <span className='marked-text'></span>
                        </Trans>
                    </div>
                    <div className='flex'>
                        <CommonButton
                            text={t("who-we-are.section-finaly.ctaButton")}
                            routePath='/contact-us'
                            containerPage='who we are'
                        />
                        {images.section14Decorations.lines}
                    </div>
                </div>
                <div className='section-14-images'>
                    <img src={images.section14Decorations.image} alt='' className="hand-pointing" />
                    <img src={images.section14Decorations.tringles} alt='' />

                </div>
            </section >
        </div >
    );
}

export default WhoWeAre;

